// PayoffChart — Strategy payoff diagram (Angel One Light Theme)
import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';

export default function PayoffChart({ legs, spotPrice }) {
  const payoffData = useMemo(() => {
    if (!legs || legs.length === 0) return [];

    const strikes = legs.map(l => l.strike).filter(Boolean);
    if (strikes.length === 0) return [];

    const minStrike = Math.min(...strikes);
    const maxStrike = Math.max(...strikes);
    const range = Math.max(maxStrike - minStrike, 100);
    const start = minStrike - range * 0.5;
    const end = maxStrike + range * 0.5;
    const step = range / 50;

    const data = [];
    for (let price = start; price <= end; price += step) {
      let pnl = 0;
      legs.forEach(leg => {
        const premium = leg.data?.lastPrice || 0;
        const qty = leg.qty || 1;
        const lotSize = 1;

        if (leg.optionType === 'CE') {
          const intrinsic = Math.max(0, price - leg.strike);
          pnl += (leg.type === 'BUY' ? (intrinsic - premium) : (premium - intrinsic)) * qty * lotSize;
        } else {
          const intrinsic = Math.max(0, leg.strike - price);
          pnl += (leg.type === 'BUY' ? (intrinsic - premium) : (premium - intrinsic)) * qty * lotSize;
        }
      });

      data.push({ price: Math.round(price), pnl: parseFloat(pnl.toFixed(2)) });
    }

    return data;
  }, [legs]);

  if (payoffData.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center text-xs text-gray-400">
        Add strategy legs to see payoff diagram
      </div>
    );
  }

  const maxProfit = Math.max(...payoffData.map(d => d.pnl));
  const maxLoss = Math.min(...payoffData.map(d => d.pnl));
  const breakevens = [];
  for (let i = 1; i < payoffData.length; i++) {
    if ((payoffData[i - 1].pnl <= 0 && payoffData[i].pnl > 0) || (payoffData[i - 1].pnl >= 0 && payoffData[i].pnl < 0)) {
      breakevens.push(payoffData[i].price);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <h4 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-2">Payoff Diagram</h4>

      <div className="flex gap-4 mb-3 text-[10px]">
        <div>
          <span className="text-gray-400">Max Profit: </span>
          <span className="text-green-600 font-mono font-semibold">
            {maxProfit === Infinity ? 'Unlimited' : `${maxProfit.toFixed(2)}`}
          </span>
        </div>
        <div>
          <span className="text-gray-400">Max Loss: </span>
          <span className="text-red-500 font-mono font-semibold">{Math.abs(maxLoss).toFixed(2)}</span>
        </div>
        {breakevens.map((be, i) => (
          <div key={i}>
            <span className="text-gray-400">BE{breakevens.length > 1 ? i + 1 : ''}: </span>
            <span className="text-blue-600 font-mono font-semibold">{be}</span>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={payoffData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="price"
            tick={{ fontSize: 9, fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 9, fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const d = payload[0].payload;
              return (
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-[10px]">
                  <p className="text-gray-600 font-medium">Spot: {d.price}</p>
                  <p className={d.pnl >= 0 ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold'}>
                    P&L: {d.pnl.toFixed(2)}
                  </p>
                </div>
              );
            }}
          />
          <ReferenceLine y={0} stroke="#D1D5DB" strokeDasharray="3 3" />
          <defs>
            <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16A34A" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#16A34A" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="pnl"
            stroke="#16A34A"
            fill="url(#profitGrad)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
